import { AfterViewInit, ChangeDetectorRef, Component, Inject, Input, NgZone, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
// amCharts imports
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { BaseChartService } from '../../../../shared/services/base-chart.service';
import { BarChartData } from '../interfaces/bar-chart-data.interface';
import { CommonService } from '../../../../shared/services/common.service';

/**
 * Draggable Pie Graph Component
 * @author: PRachethan | 02-08-2025 
 */
@Component({
  selector: 'bi-draggable-pie-graph',
  standalone: false,
  templateUrl: './draggable-pie-graph.component.html',
  styleUrl: './draggable-pie-graph.component.scss'
})
export class DraggablePieGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  private root!: am5.Root;
  data: BarChartData[] = [];
  private draggablePiegraphReportSubscription: any;
  private isDraggablePieViewInitialized = false;
  private isCreatingChart = false;
  @Input() reportData: any = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private _baseChartService: BaseChartService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _commonService: CommonService) { }

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.draggablePiegraphReportSubscription = this._commonService.graphReportState.subscribe((data: any) => {
        if (data && data.reportData && Array.isArray(data.reportData) && data.reportData.length > 0) {
          this.reportData = data;
          console.log("Draggable Pie Graph Data", this.reportData);
          this.data = this._baseChartService.transformReportData(data);
          if (this.isDraggablePieViewInitialized) {
            this.updateChart(this.data);
          }
        } else {
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.isDraggablePieViewInitialized = true;
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        // Check if DOM element exists before creating chart
        const chartContainer = document.getElementById('draggable-pie-chartdiv');
        if (chartContainer) {
          this.data = this._baseChartService.transformReportData(this.reportData);
          this.updateChart(this.data);
        } else {
          console.log('Draggable Pie Chart - DOM element not ready in ngAfterViewInit, will retry later');
        }
      });
    }
  }

  ngOnChanges(): void {
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0 && this.isDraggablePieViewInitialized) {
      this.zone.runOutsideAngular(() => {
        // Check if DOM element exists before creating chart
        const chartContainer = document.getElementById('draggable-pie-chartdiv');
        if (chartContainer) {
          this.data = this._baseChartService.transformReportData(this.reportData);
          this.updateChart(this.data);
        } else {
          console.log('Draggable Pie Chart - DOM element not ready in ngOnChanges, will retry later');
          // Retry after a short delay
          setTimeout(() => {
            const retryContainer = document.getElementById('draggable-pie-chartdiv');
            if (retryContainer) {
              this.data = this._baseChartService.transformReportData(this.reportData);
              this.updateChart(this.data);
            }
          }, 100);
        }
      });
    }
  }

  createChart(data: BarChartData[]) {
    // Prevent multiple chart creations
    if (this.isCreatingChart) {
      console.log('Draggable Pie Chart - Chart creation already in progress, skipping...');
      return;
    }

    this.isCreatingChart = true;

    // Check if the chart container exists
    const chartContainer = document.getElementById('draggable-pie-chartdiv');
    if (!chartContainer) {
      console.error('Draggable Pie Chart - Chart container not found! Make sure element with id "draggable-pie-chartdiv" exists');
      this.isCreatingChart = false;
      return;
    }

    console.log('Draggable Pie Chart - Container found, creating chart...');

    // Dispose existing chart before creating new one
    if (this.root) {
      console.log('Draggable Pie Chart - Disposing existing chart...');
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }

    try {
      console.log('Draggable Pie Chart - Creating new chart with data:', data);
      
      this.root = this._baseChartService.createRoot('draggable-pie-chartdiv');
      this._baseChartService.attachExporting(this.root, { 
        filePrefix: 'draggable-pie',
        data: data
      });
      console.log('Draggable Pie Chart - Root created:', this.root);

      // Create custom theme
      let myTheme = am5.Theme.new(this.root);
      myTheme.rule("Label").set("fontSize", "0.8em");

      // Set themes
      this.root.setThemes([
        am5themes_Animated.new(this.root),
        myTheme
      ]);

      // Create vertical wrapper (charts row + legend at bottom)
      let wrapper = this.root.container.children.push(am5.Container.new(this.root, {
        width: am5.p100,
        height: am5.p100,
        layout: this.root.verticalLayout
      }));

      // Horizontal container for the two pie charts (flex so legend gets space below)
      let container = wrapper.children.push(am5.Container.new(this.root, {
        width: am5.p100,
        height: am5.percent(85),
        layout: this.root.horizontalLayout
      }));

      console.log('Draggable Pie Chart - Container created');

      // Transform data for pie chart
      const categoryField = this._baseChartService.getCategoryField(data);
      const valueFields = this._baseChartService.getValueFields(data);
      const firstValueField = valueFields[0];

      console.log('Draggable Pie Chart - Category field:', categoryField, 'Value field:', firstValueField);

      // Create data with dummy slice
      let pieData;
      if (data && data.length > 0 && categoryField && firstValueField) {
        pieData = [
          {
            category: "Dummy",
            value: 1000,
            settings: {
              fill: am5.color(0xdadada),
              stroke: am5.color(0xdadada),
              fillOpacity: 0.3,
              strokeDasharray: [4, 4],
              tooltipText: null,
              draggable: false
            },
            dummyLabelSettings: {
              forceHidden: true
            }
          },
          ...data.map((item, index) => ({
            category: item[categoryField],
            value: Number(item[firstValueField]) || 0
          }))
        ];
      } else {
        // Fallback data if transformation fails
        console.log('Draggable Pie Chart - Using fallback data');
        pieData = [
          {
            category: "Dummy",
            value: 1000,
            settings: {
              fill: am5.color(0xdadada),
              stroke: am5.color(0xdadada),
              fillOpacity: 0.3,
              strokeDasharray: [4, 4],
              tooltipText: null,
              draggable: false
            },
            dummyLabelSettings: {
              forceHidden: true
            }
          },
          {
            category: "Test 1",
            value: 300
          },
          {
            category: "Test 2", 
            value: 200
          },
          {
            category: "Test 3",
            value: 150
          }
        ];
      }

      console.log("Transformed pie data:", pieData);

      // Create first chart
      let chart0 = container.children.push(am5percent.PieChart.new(this.root, {
        innerRadius: am5.p50,
        tooltip: am5.Tooltip.new(this.root, {})
      }));

      console.log('Draggable Pie Chart - First chart created');

      // Create series for first chart
      let series0 = chart0.series.push(am5percent.PieSeries.new(this.root, {
        valueField: "value",
        categoryField: "category",
        alignLabels: false
      }));

      series0.labels.template.setAll({
        textType: "circular",
        templateField: "dummyLabelSettings",
        radius: 30,
        centerX: am5.p50,
        centerY: am5.p50,
        fontSize: 11,
        fill: am5.color(0x000000),
        forceHidden: false,
        oversizedBehavior: "wrap",
        breakWords: true,
        maxWidth: 80,
        text: "{value}"
      });

      series0.ticks.template.set("forceHidden", true);

      let sliceTemplate0 = series0.slices.template;
      sliceTemplate0.setAll({
        draggable: true,
        templateField: "settings",
        cornerRadius: 5,
        tooltipText: "{category}: {valuePercentTotal.formatNumber('0.00')}% ({value})"
      });

      // Separator line
      container.children.push(am5.Line.new(this.root, {
        layer: 1,
        height: am5.percent(60),
        y: am5.p50,
        centerY: am5.p50,
        strokeDasharray: [4, 4],
        stroke: this.root.interfaceColors.get("alternativeBackground"),
        strokeOpacity: 0.5
      }));

      // Label
      container.children.push(am5.Label.new(this.root, {
        layer: 1,
        text: "Drag slices over the line",
        y: am5.p50,
        textAlign: "center",
        rotation: -90,
        isMeasured: false
      }));

      // Create second chart
      let chart1 = container.children.push(am5percent.PieChart.new(this.root, {
        innerRadius: am5.p50,
        tooltip: am5.Tooltip.new(this.root, {})
      }));

      console.log('Draggable Pie Chart - Second chart created');

      // Create series for second chart
      let series1 = chart1.series.push(am5percent.PieSeries.new(this.root, {
        valueField: "value",
        categoryField: "category",
        alignLabels: false
      }));

      series1.labels.template.setAll({
        textType: "circular",
        radius: 30,
        templateField: "dummyLabelSettings",
        centerX: am5.p50,
        centerY: am5.p50,
        fontSize: 11,
        fill: am5.color(0x000000),
        forceHidden: false,
        oversizedBehavior: "wrap",
        breakWords: true,
        maxWidth: 80
      });

      series1.ticks.template.set("forceHidden", true);

      let sliceTemplate1 = series1.slices.template;
      sliceTemplate1.setAll({
        draggable: true,
        templateField: "settings",
        cornerRadius: 5,
        tooltipText: "{category}: {valuePercentTotal.formatNumber('0.00')}% ({value})"
      });

      let previousDownSlice: any;

      // change layers when down
      sliceTemplate0.events.on("pointerdown", function (e) {
        if (previousDownSlice) {
          // previousDownSlice.set("layer", 0);
        }
        e.target.set("layer", 1);
        previousDownSlice = e.target;
      });

      sliceTemplate1.events.on("pointerdown", function (e) {
        if (previousDownSlice) {
          // previousDownSlice.set("layer", 0);
        }
        e.target.set("layer", 1);
        previousDownSlice = e.target;
      });

      // when released, do all the magic
      sliceTemplate0.events.on("pointerup", function (e) {
        series0.hideTooltip();
        series1.hideTooltip();

        let slice = e.target;
        if (slice.x() > container.width() / 4) {
          let index = series0.slices.indexOf(slice);
          if (slice.dataItem) {
            slice.dataItem.hide();
          }

          let series1DataItem = series1.dataItems[index];
          series1DataItem.show();
          series1DataItem.get("slice").setAll({ x: 0, y: 0 });

          handleDummy(series0);
          handleDummy(series1);
        } else {
          slice.animate({
            key: "x",
            to: 0,
            duration: 500,
            easing: am5.ease.out(am5.ease.cubic)
          });
          slice.animate({
            key: "y",
            to: 0,
            duration: 500,
            easing: am5.ease.out(am5.ease.cubic)
          });
        }
      });

      sliceTemplate1.events.on("pointerup", function (e) {
        let slice = e.target;

        series0.hideTooltip();
        series1.hideTooltip();

        if (slice.x() < container.width() / 4) {
          let index = series1.slices.indexOf(slice);
          if (slice.dataItem) {
            slice.dataItem.hide();
          }

          let series0DataItem = series0.dataItems[index];
          series0DataItem.show();
          series0DataItem.get("slice").setAll({ x: 0, y: 0 });

          handleDummy(series0);
          handleDummy(series1);
        } else {
          slice.animate({
            key: "x",
            to: 0,
            duration: 500,
            easing: am5.ease.out(am5.ease.cubic)
          });
          slice.animate({
            key: "y",
            to: 0,
            duration: 500,
            easing: am5.ease.out(am5.ease.cubic)
          });
        }
      });

      // show/hide dummy slice depending if there are other visible slices
      function handleDummy(series: any) {
        // count visible data items
        let visibleCount = 0;
        am5.array.each(series.dataItems, function (dataItem: any) {
          if (!dataItem.isHidden()) {
            visibleCount++;
          }
        });
        // if all hidden, show dummy
        if (visibleCount == 0) {
          series.dataItems[0].show();
        } else {
          series.dataItems[0].hide();
        }
      }

      // set data
      series0.data.setAll(pieData);
      series1.data.setAll(pieData);

      console.log('Draggable Pie Chart - Data set for both series');

      // Default: first real slice (index 1) is shown on the right chart; rest on the left
      // series0 (left): hide dummy and first real slice so it appears only on the right
      series0.dataItems[0].hide(0);
      if (series0.dataItems.length > 1) {
        series0.dataItems[1].hide(0);
      }

      // series1 (right): hide all except first real slice; handleDummy will hide dummy when any slice is visible
      am5.array.each(series1.dataItems, function (dataItem: any, index: number) {
        if (index > 1) {
          dataItem.hide(0);
        }
      });
      handleDummy(series0);
      handleDummy(series1);

      // Add legend at the bottom (exclude dummy slice)
      let legend = wrapper.children.push(this._baseChartService.createLegend(this.root));
      legend.set("marginTop", 10);
      legend.markers.template.setAll({ width: 12, height: 12 });
      const legendData = series0.dataItems.filter((di: any) => di.get("category") !== "Dummy");
      legend.data.setAll(legendData);
      this._baseChartService.setupLegendAnimation(legend);

      // reveal wrapper
      wrapper.appear(1000, 100);

      console.log('Draggable Pie Chart - Chart created successfully');
      this.isCreatingChart = false;

    } catch (error) {
      console.error('Error creating draggable pie chart:', error);
      this.isCreatingChart = false;
    }
  }

  private updateChart(data: BarChartData[]) {
    // Prevent multiple chart updates
    if (this.isCreatingChart) {
      console.log('Draggable Pie Chart - Chart update skipped, chart creation in progress...');
      return;
    }

    // Check if DOM element exists before proceeding
    const chartContainer = document.getElementById('draggable-pie-chartdiv');
    if (!chartContainer) {
      console.log('Draggable Pie Chart - DOM element not ready in updateChart, will retry later');
      // Retry after a short delay
      setTimeout(() => {
        this.updateChart(data);
      }, 100);
      return;
    }

    // Dispose existing chart first
    if (this.root) {
      console.log('Draggable Pie Chart - Disposing chart in updateChart...');
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }
    
    // Add a longer delay to ensure proper disposal and DOM element availability
    setTimeout(() => {
      this.createChart(data);
    }, 200);
    
    // Update the chart data in the registry
    BaseChartService.updateActiveChartData(data);
  }

  ngOnDestroy(): void {
    this._baseChartService.disposeChart(this.root);
    if (this.draggablePiegraphReportSubscription) {
      this.draggablePiegraphReportSubscription.unsubscribe();
    }
  }
}
